import React, { useCallback, useMemo, useState } from "react";

export const AlertContext = React.createContext({});

/**
 * The alert context provider
 * 
 * @param {Object} props - The props of the component
 * @returns The alert context provider
 */
const AlertProvider = ({ children }) => {
  const [alertList, setAlertList] = useState([]);

  /**
   * Alerts the user with a toast
   * 
   * @param {Object} props The alert properties
   */
  const alert = useCallback(
    ({
      id,
      title = "",
      description = "",
      status = "success",
      duration = 5000,
    }) => {
      const uuid = crypto.randomUUID();
      const newAlert = {
        uuid,
        id,
        title,
        description,
        status,
      };

      setAlertList((alertList) => [...alertList, newAlert]);
      setTimeout(() => {
        setAlertList((alertList) =>
          alertList.filter((currentAlert) => currentAlert.uuid !== uuid),
        );
      }, duration);
    },
    [],
  );

  /**
   * Removes an alert
   * 
   * @param {string} uuid - The uuid of the alert to remove
   */
  const removeAlert = useCallback((uuid) => {
    setAlertList((alertList) =>
      alertList.filter((currentAlert) => currentAlert.uuid !== uuid),
    );
  }, []);

  /**
   * Checks if an alert is active
   * 
   * @param {string} id - The id of the alert to check
   * @returns {boolean} - True if the alert is active, false otherwise
   */
  const isActive = useCallback(
    (id) => !!alertList.find((currentAlert) => currentAlert.id === id),
    [alertList],
  );

  const contextValue = useMemo(() => {
    alert.isActive = isActive;

    return {
      alertList,
      alert,
      removeAlert,
    };
  }, [alert, alertList, isActive, removeAlert]);

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
    </AlertContext.Provider>
  );
};

export default React.memo(AlertProvider);
